(() => {
  const PAYLOAD_CACHE_KEY = '__bpAddonsPayload';

  const parseJson = (el, fallback) => {
    if (!el) return fallback;
    try {
      const raw = (el.textContent || '').trim();
      if (!raw) return fallback;
      let parsed = JSON.parse(raw);
      // Handle double-encoded payloads where JSON is wrapped as a string.
      if (typeof parsed === 'string') {
        const nested = parsed.trim();
        if (
          (nested.startsWith('{') && nested.endsWith('}')) ||
          (nested.startsWith('[') && nested.endsWith(']'))
        ) {
          parsed = JSON.parse(nested);
        }
      }
      return parsed;
    } catch (_) {
      return fallback;
    }
  };

  const asText = (value) => String(value == null ? '' : value).trim();
  const asArray = (value) => (Array.isArray(value) ? value : []);
  const toInt = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : fallback;
  };

  const sek = new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const productCache = new Map();

  const fetchProductJson = async (handle) => {
    const key = asText(handle);
    if (!key) return null;
    if (productCache.has(key)) return productCache.get(key);

    try {
      const res = await fetch(`/products/${encodeURIComponent(key)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        productCache.set(key, null);
        return null;
      }
      const row = await res.json();
      productCache.set(key, row || null);
      return row || null;
    } catch (_) {
      productCache.set(key, null);
      return null;
    }
  };

  const findVariantInput = (root) => {
    const scope = root.closest('section, .shopify-section, [data-section-id]') || document;
    return (
      scope.querySelector('form[action*="/cart/add"] [name="id"]') ||
      scope.querySelector('[name="id"]') ||
      null
    );
  };

  const selectedVariantId = (root) => {
    const input = findVariantInput(root);
    if (input && asText(input.value)) return asText(input.value);
    try {
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      return asText(fromUrl);
    } catch (_) {
      return '';
    }
  };

  const getCurrentParentSku = async (root, handle) => {
    const product = await fetchProductJson(handle);
    if (!product) return '';
    const variants = asArray(product.variants);
    if (!variants.length) return '';
    const id = selectedVariantId(root);
    const row = variants.find((v) => asText(v && v.id) === id) || variants[0];
    return asText(row && row.sku);
  };

  const getBaseQuantity = (root) => {
    const scope = root.closest('section, .shopify-section, [data-section-id]') || document;
    const qtyInput =
      scope.querySelector('form[action*="/cart/add"] [name="quantity"]') ||
      scope.querySelector('[name="quantity"]');
    if (!qtyInput) return 1;
    const parsed = toInt(qtyInput.value, 1);
    return parsed > 0 ? parsed : 1;
  };

  const pickVariant = (product, skuHint) => {
    const variants = asArray(product && product.variants);
    if (!variants.length) return null;

    const hint = asText(skuHint).toLowerCase();
    if (hint) {
      const exact = variants.find((v) => asText(v && v.sku).toLowerCase() === hint);
      if (exact) return exact;
      const partial = variants.find((v) => asText(v && v.sku).toLowerCase().includes(hint));
      if (partial) return partial;
    }

    return variants.find((v) => !!v && v.available) || variants[0];
  };

  const getVariantImage = (product, variant, fallbackImage) => {
    if (asText(fallbackImage)) return fallbackImage;

    const vImg = variant && variant.featured_image;
    if (typeof vImg === 'string' && asText(vImg)) return vImg;
    if (vImg && typeof vImg === 'object') {
      if (asText(vImg.src)) return asText(vImg.src);
      if (asText(vImg.url)) return asText(vImg.url);
    }

    const pImg = product && product.featured_image;
    if (typeof pImg === 'string' && asText(pImg)) return pImg;
    if (pImg && typeof pImg === 'object') {
      if (asText(pImg.src)) return asText(pImg.src);
      if (asText(pImg.url)) return asText(pImg.url);
    }

    const imgs = asArray(product && product.images);
    const first = imgs[0];
    if (typeof first === 'string' && asText(first)) return first;
    if (first && typeof first === 'object') {
      return asText(first.src || first.url || '');
    }
    return '';
  };

  const formatPrice = (priceCents) => {
    const cents = toInt(priceCents, 0);
    return sek.format(cents / 100);
  };

  const setStatus = (root, text, cls = '') => {
    const node = root.querySelector('[data-bp-addons-status]');
    if (!node) return;
    node.className = `bp-addons__status ${cls}`.trim();
    node.textContent = asText(text);
  };

  const resolveAllowedHandles = (payload, parentSku) => {
    const map = payload && typeof payload.by_parent_sku === 'object' ? payload.by_parent_sku : null;
    if (!map) return null;
    const key = asText(parentSku);
    const list = key ? asArray(map[key]) : [];
    if (!list.length) return null;
    return new Set(list.map((row) => asText(row)).filter(Boolean));
  };

  const createEl = (tag, cls, text = '') => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text) el.textContent = text;
    return el;
  };

  const variantLabel = (variant) => {
    const t = asText(variant && variant.title);
    return t && t !== 'Default Title' ? t : '';
  };

  const renderAddonCard = (item, product, payload) => {
    const firstVariant = pickVariant(product, item.sku_hint);
    if (!firstVariant) return null;

    const variants = asArray(product.variants);
    const allZero = variants.length > 0 && variants.every((row) => toInt(row && row.price, 0) <= 0);
    if (allZero && asText(payload.zero_price_policy) === 'hide-by-default') return null;

    const card = createEl('article', 'bp-addon-card');
    card.dataset.handle = asText(product.handle || item.handle);

    const media = createEl('div', 'bp-addon-card__media');
    const imgSrc = getVariantImage(product, firstVariant, asText(item.image_hint_src));
    if (imgSrc) {
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = asText(item.title || product.title || 'Tillval');
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.appendChild(createEl('div', 'bp-addon-card__placeholder', 'Bild kommer snart'));
    }
    card.appendChild(media);

    const body = createEl('div', 'bp-addon-card__body');
    body.appendChild(createEl('h4', 'bp-addon-card__title', asText(item.title || product.title || 'Tillval')));

    const metaBits = [];
    if (asText(item.size_hint)) metaBits.push(asText(item.size_hint));
    const firstVariantLabel = variantLabel(firstVariant);
    if (firstVariantLabel) metaBits.push(firstVariantLabel);
    body.appendChild(createEl('p', 'bp-addon-card__meta', metaBits.join(' • ')));

    const priceNode = createEl('p', 'bp-addon-card__price');
    body.appendChild(priceNode);

    let select = null;
    if (variants.length > 1) {
      select = createEl('select', 'bp-addon-card__variant');
      select.setAttribute('aria-label', `Välj variant för ${asText(item.title || product.title || 'tillval')}`);
      variants.forEach((variant) => {
        const opt = document.createElement('option');
        opt.value = asText(variant && variant.id);
        opt.dataset.price = String(toInt(variant && variant.price, 0));
        opt.dataset.image = getVariantImage(product, variant, '') || '';
        const label = variantLabel(variant);
        opt.textContent = `${label || 'Variant'} · ${formatPrice(opt.dataset.price)}`;
        if (asText(variant && variant.id) === asText(firstVariant.id)) {
          opt.selected = true;
        }
        select.appendChild(opt);
      });
      body.appendChild(select);
    }

    const actionWrap = createEl('div', 'bp-addon-card__actions');
    const addLabel = createEl('label', 'bp-addon-card__add');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.dataset.addonInclude = '1';
    checkbox.checked = true;
    addLabel.appendChild(checkbox);
    addLabel.appendChild(createEl('span', '', 'Ta med i paket'));
    actionWrap.appendChild(addLabel);
    body.appendChild(actionWrap);

    const note = createEl('p', 'bp-addon-card__note');
    body.appendChild(note);

    const updateFromVariant = () => {
      const variantId = select ? asText(select.value) : asText(firstVariant.id);
      const variant = variants.find((row) => asText(row && row.id) === variantId) || firstVariant;
      const cents = toInt(variant && variant.price, 0);
      card.dataset.variantId = asText(variant && variant.id);
      card.dataset.priceCents = String(cents);

      priceNode.textContent = formatPrice(cents);
      priceNode.classList.toggle('is-zero', cents <= 0);

      const isZero = cents <= 0;
      if (isZero) {
        note.textContent = 'Informationsrad (0 kr). Läggs inte i varukorgen.';
        checkbox.checked = false;
        checkbox.disabled = true;
        card.classList.add('is-disabled');
      } else {
        note.textContent = '';
        checkbox.disabled = false;
        card.classList.remove('is-disabled');
      }

      const nextImage =
        (select && select.selectedOptions[0] && asText(select.selectedOptions[0].dataset.image)) || '';
      if (nextImage) {
        const img = media.querySelector('img');
        if (img) img.src = nextImage;
      }
    };

    if (select) {
      select.addEventListener('change', updateFromVariant);
    }
    updateFromVariant();

    card.appendChild(body);
    return card;
  };

  const renderAddonFallbackCard = (item) => {
    const card = createEl('article', 'bp-addon-card is-disabled');
    card.dataset.handle = asText(item.handle);

    const media = createEl('div', 'bp-addon-card__media');
    const imgSrc = asText(item.image_hint_src);
    if (imgSrc) {
      const img = document.createElement('img');
      img.src = imgSrc;
      img.alt = asText(item.title || 'Tillval');
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.appendChild(createEl('div', 'bp-addon-card__placeholder', 'Bild kommer snart'));
    }
    card.appendChild(media);

    const body = createEl('div', 'bp-addon-card__body');
    body.appendChild(createEl('h4', 'bp-addon-card__title', asText(item.title || 'Tillval')));
    body.appendChild(createEl('p', 'bp-addon-card__meta', asText(item.size_hint)));

    const priceNode = createEl('p', 'bp-addon-card__price is-zero');
    const hint = Number(asText(item.price_hint || '').replace(',', '.'));
    if (Number.isFinite(hint) && hint > 0) {
      priceNode.classList.remove('is-zero');
      priceNode.textContent = sek.format(hint);
    } else {
      priceNode.textContent = 'Pris kommer';
    }
    body.appendChild(priceNode);

    const actionWrap = createEl('div', 'bp-addon-card__actions');
    const addLabel = createEl('label', 'bp-addon-card__add');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.dataset.addonInclude = '1';
    checkbox.checked = false;
    checkbox.disabled = true;
    addLabel.appendChild(checkbox);
    addLabel.appendChild(createEl('span', '', 'Tillfälligt ej köpbar'));
    actionWrap.appendChild(addLabel);
    body.appendChild(actionWrap);

    const note = createEl(
      'p',
      'bp-addon-card__note',
      'Detta tillval är mappat men inte publicerat som köpbar produkt ännu.'
    );
    body.appendChild(note);

    card.appendChild(body);
    return card;
  };

  const mountSlider = (root, list, cardCount) => {
    const slider = createEl('div', 'bp-addons__slider');
    const prev = createEl('button', 'bp-addons__nav bp-addons__nav--prev', '‹');
    const next = createEl('button', 'bp-addons__nav bp-addons__nav--next', '›');
    prev.type = 'button';
    next.type = 'button';
    prev.setAttribute('aria-label', 'Föregående tillval');
    next.setAttribute('aria-label', 'Nästa tillval');

    const viewport = createEl('div', 'bp-addons__viewport');
    viewport.appendChild(list);

    const step = () => Math.max(220, Math.round(viewport.clientWidth * 0.85));
    const updateNav = () => {
      const maxLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const left = Math.max(0, viewport.scrollLeft);
      prev.disabled = left <= 2;
      next.disabled = left >= maxLeft - 2;
    };

    prev.addEventListener('click', () => {
      viewport.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      viewport.scrollBy({ left: step(), behavior: 'smooth' });
    });
    viewport.addEventListener('scroll', updateNav, { passive: true });
    window.setTimeout(updateNav, 0);

    if (cardCount <= 1) {
      prev.hidden = true;
      next.hidden = true;
    }

    slider.appendChild(prev);
    slider.appendChild(viewport);
    slider.appendChild(next);
    root.appendChild(slider);
  };

  const renderRoot = async (root) => {
    let payload = root[PAYLOAD_CACHE_KEY] || null;
    if (!payload) {
      payload = parseJson(root.querySelector('[data-bp-addons-payload]'), null);
      if (payload) {
        root[PAYLOAD_CACHE_KEY] = payload;
      }
    }

    if (!payload) {
      root.hidden = false;
      root.innerHTML = '';
      root.appendChild(createEl('p', 'bp-addons__empty', 'Tillval kunde inte läsas in för denna produkt.'));
      return;
    }
    if (!payload.enabled) {
      root.hidden = false;
      root.innerHTML = '';
      root.appendChild(createEl('p', 'bp-addons__empty', 'Inga tillval är definierade för denna produkt.'));
      return;
    }

    const allItems = asArray(payload.items).filter((row) => asText(row && row.handle));
    if (!allItems.length) {
      root.hidden = false;
      root.innerHTML = '';
      root.appendChild(createEl('p', 'bp-addons__empty', 'Tillval saknar produktkopplingar just nu.'));
      return;
    }

    const currentHandle = asText(root.dataset.bpProductHandle);
    const parentSku = currentHandle ? await getCurrentParentSku(root, currentHandle) : '';
    root.dataset.bpParentSku = parentSku;
    const allowed = resolveAllowedHandles(payload, parentSku);
    const candidateItems = allowed
      ? allItems.filter((row) => allowed.has(asText(row.handle)))
      : allItems;

    const products = await Promise.all(
      candidateItems.map(async (item) => {
        const product = await fetchProductJson(item.handle);
        return { item, product };
      })
    );

    const section = createEl('section', 'bp-addons__head');
    section.appendChild(createEl('h3', '', asText(payload.title || 'Välj tillval')));
    if (asText(payload.intro)) {
      section.appendChild(createEl('p', '', asText(payload.intro)));
    }
    if (allowed) {
      const total = allItems.length;
      const matched = candidateItems.length;
      section.appendChild(
        createEl(
          'p',
          'bp-addons__compat',
          `Visar ${matched} av ${total} tillval som passar vald variant.`
        )
      );
    }

    const list = createEl('div', 'bp-addons__list');
    let cardCount = 0;
    products.forEach(({ item, product }) => {
      const card =
        product && asText(product.handle)
          ? renderAddonCard(item, product, payload)
          : renderAddonFallbackCard(item);
      if (!card) return;
      list.appendChild(card);
      cardCount += 1;
    });

    root.innerHTML = '';
    root.appendChild(section);

    if (!cardCount) {
      const empty = createEl(
        'p',
        'bp-addons__empty',
        allowed
          ? 'Inga tillval matchar vald variant just nu. Byt variant för att se kompatibla tillval.'
          : 'Inga valbara tillval hittades för denna produkt just nu.'
      );
      root.appendChild(empty);
      return;
    }

    mountSlider(root, list, cardCount);

    const footer = createEl('div', 'bp-addons__footer');
    const button = createEl('button', 'bp-addons__btn', 'Lägg valda tillval i varukorg');
    button.type = 'button';
    button.dataset.bpAddonsSubmit = '1';
    footer.appendChild(button);
    footer.appendChild(createEl('p', 'bp-addons__status', ''));
    footer.querySelector('.bp-addons__status').setAttribute('data-bp-addons-status', '1');
    root.appendChild(footer);

    button.addEventListener('click', async () => {
      const rows = Array.from(root.querySelectorAll('.bp-addon-card'));
      const parentVariantId = selectedVariantId(root);
      const parentHandle = asText(root.dataset.bpProductHandle);
      const parentSku = asText(root.dataset.bpParentSku);
      const baseQty = getBaseQuantity(root);
      const items = rows
        .map((card) => {
          const checkbox = card.querySelector('[data-addon-include]');
          if (!checkbox || !checkbox.checked || checkbox.disabled) return null;
          const variantId = asText(card.dataset.variantId);
          if (!variantId) return null;
          return {
            id: Number(variantId),
            quantity: baseQty,
            properties: {
              _bp_tillval_for_handle: parentHandle,
              _bp_tillval_for_variant_id: parentVariantId,
              _bp_tillval_for_sku: parentSku,
            },
          };
        })
        .filter(Boolean);

      if (!items.length) {
        setStatus(root, 'Välj minst ett tillval att lägga i varukorgen.', 'is-warn');
        return;
      }

      button.disabled = true;
      setStatus(root, 'Lägger till tillval…');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const raw = await res.text();
          throw new Error(raw || `HTTP ${res.status}`);
        }
        setStatus(root, `Klart. ${items.length} tillval tillagda i varukorgen.`, 'is-ok');
        document.dispatchEvent(
          new CustomEvent('bp:addons:added', {
            detail: { count: items.length },
          })
        );
      } catch (_) {
        setStatus(root, 'Misslyckades att lägga till tillval. Försök igen.', 'is-error');
      } finally {
        button.disabled = false;
      }
    });
  };

  const initRoot = (root) => {
    if (!root || root.dataset.bpAddonsReady === '1') return;
    root.dataset.bpAddonsReady = '1';

    const state = { running: false, queued: false, hasRendered: false, lastVariantId: '' };
    const run = async (force = false) => {
      const currentVariantId = selectedVariantId(root) || '__no_variant__';
      if (!force && state.hasRendered && currentVariantId === state.lastVariantId) {
        return;
      }
      state.lastVariantId = currentVariantId;

      if (state.running) {
        state.queued = true;
        return;
      }
      state.running = true;
      try {
        await renderRoot(root);
        state.hasRendered = true;
      } finally {
        state.running = false;
        if (state.queued) {
          state.queued = false;
          run();
        }
      }
    };

    run();

    const onChange = () => run();
    document.addEventListener('change', onChange, true);
    const timer = window.setInterval(() => {
      if (!document.contains(root)) {
        window.clearInterval(timer);
        document.removeEventListener('change', onChange, true);
        return;
      }
      run();
    }, 500);
  };

  const initAll = (scope) => {
    const base = scope && scope.querySelectorAll ? scope : document;
    base.querySelectorAll('[data-bp-addons]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
